import React, { useEffect, useMemo, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

// Soft UI Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import Select2Field from "components/Select2Field";

// Soft UI Dashboard examples/layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { useFormik } from "formik";
import PropTypes from "prop-types";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";
import api from "services/api";
import { apiurl } from "utils";
import axios from "axios";
import { useTranslate } from "utils/i18n";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import {
  parseListPayload,
  extractLeaveListStats,
  totalPagesFromCount,
} from "utils/paginationStats";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

const withTrailingSlash = (value) => (value.endsWith("/") ? value : `${value}/`);
const leaveApiBase = withTrailingSlash(process.env.REACT_APP_LEAVE_API_BASE || apiurl);
const leaveListUrl = `${leaveApiBase}LeaveRequests/list`;
const leaveCreateUrl = `${leaveApiBase}LeaveRequests/Add`;
const leaveTypesUrl = `${leaveApiBase}LeaveRequests/leavetypes`;
const leaveUpdateUrl = `${leaveApiBase}LeaveRequests/Update`;
const STATUS_OPTIONS = ["Pending", "Approved", "Rejected", "Cancelled"];

const dialogPaperProps = {
  sx: {
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
  },
};

const fieldLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

const leaveStatWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(118, 75, 162, 0.08) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "rgba(118, 75, 162, 0.28)",
      boxShadow: "0 16px 42px rgba(118, 75, 162, 0.12)",
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const makeOptions = (data, ids, labels) =>
  data.map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));

const formatDate = (value) => {
  if (!value) return "--";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, "yyyy-MM-dd");
};

function LeaveRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [teacherOpts, setTeacherOpts] = useState([]);
  const [leaveTypeOpts, setLeaveTypeOpts] = useState([]);
  const [statusSelections, setStatusSelections] = useState({});
  const [updating, setUpdating] = useState({});
  const [updateError, setUpdateError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTargetId, setStatusTargetId] = useState(null);
  const [statusValue, setStatusValue] = useState("Pending");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [listStats, setListStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  });
  const [pageSize, setPageSize] = useState(10);
  const { t } = useTranslate();
  const requiredMessage = t("validation.required", "Required");

  const token = useMemo(() => localStorage.getItem("tk446655440000"), []);
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchTeachers = async () => {
    try {
      const { data } = await axios.get(`${apiurl}Assignment/teachers`, {
        headers: authHeader,
      });
      const teacherOptions = (Array.isArray(data) ? data : []).map((item) => {
        const id = item?.id ?? item?.Id ?? item?.teacherId ?? item?.TeacherId ?? "";
        const firstName = item?.name ?? item?.firstName ?? item?.Name ?? "";
        const lastName = item?.preName ?? item?.lastName ?? item?.PreName ?? "";
        const name =
          [firstName, lastName].filter(Boolean).join(" ") ||
          item?.teacher ||
          item?.fullName ||
          "";
        const regNumber = item?.regNumber ?? item?.RegNumber ?? item?.matriculate ?? "";
        const phone = item?.phone ?? item?.Phone ?? item?.phoneNumber ?? "";
        return {
          id,
          text: [regNumber, name, phone].filter(Boolean).join(" - "),
        };
      });
      setTeacherOpts(teacherOptions);
    } catch (err) {
      console.error("Failed to fetch teachers", err);
    }
  };

  const truncateText = (text, limit = 40) => {
    if (!text) return "--";
    if (text.length <= limit) return text;
    return `${text.substring(0, limit)}...`;
  };

  const renderStatus = (status) => t(`status.${status}`, status || "--");

  const leaveStats = listStats;

  const assistantPageSnapshot = useMemo(
    () => ({
      pageKey: "leave-requests",
      route: "/leave-requests",
      filters: { page, pageSize, total: totalCount, pending: leaveStats.pending },
    }),
    [page, pageSize, totalCount, leaveStats.pending]
  );
  useRegisterAssistantPage(assistantPageSnapshot);

  const totalPages = totalPagesFromCount(totalCount, pageSize);

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const tableRangeLabel =
    totalCount === 0
      ? null
      : t("leave.table_range", "{{start}}–{{end}} of {{total}} requests")
          .replace("{{start}}", String((page - 1) * pageSize + 1))
          .replace("{{end}}", String(Math.min(page * pageSize, totalCount)))
          .replace("{{total}}", String(totalCount));

  const statusChipSx = (status) => {
    const s = String(status || "Pending");
    const base = { fontWeight: 600, fontSize: "0.72rem" };
    if (s === "Approved")
      return { ...base, bgcolor: figmaTokens.badgeApproved, color: "#fff" };
    if (s === "Pending")
      return { ...base, bgcolor: figmaTokens.badgePending, color: "#1e293b" };
    if (s === "Rejected")
      return { ...base, bgcolor: figmaTokens.badgeDanger, color: "#fff" };
    if (s === "Cancelled")
      return {
        ...base,
        bgcolor: "rgba(100, 116, 139, 0.14)",
        color: figmaTokens.textSecondary,
      };
    return { ...base, bgcolor: figmaTokens.borderSubtle, color: figmaTokens.textPrimary };
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetailData(null);
    try {
      const { data } = await api.get(leaveListUrl, {
        params: { Id: id },
        headers: authHeader,
      });
      const item = Array.isArray(data?.data) ? data.data[0] : data?.data;
      setDetailData(item || null);
    } catch (err) {
      setDetailError(
        err?.response?.data?.message || "Failed to load leave request details."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const openStatusModal = (row) => {
    setStatusTargetId(row.id);
    setStatusValue(row.status || "Pending");
    setStatusModalOpen(true);
  };

  const fetchLeaveTypes = async () => {
    try {
      const { data } = await api.get(leaveTypesUrl, { headers: authHeader });
      setLeaveTypeOpts(
        makeOptions(data?.data || [], ["id"], ["name"])
      );
    } catch (err) {
      console.error("Failed to fetch leave types", err);
    }
  };

  const fetchRequests = async (pageNumber = page) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(leaveListUrl, {
        params: { pageNumber, pageSize },
        headers: authHeader,
      });
      const list = parseListPayload(data);
      setRequests(list);
      const stats = extractLeaveListStats(list);
      setListStats(stats);
      setTotalCount(stats.total || list.length);
      const map = {};
      list.forEach((item) => {
        map[item.id] = item.status || "Pending";
      });
      setStatusSelections(map);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load leave requests.");
      setRequests([]);
      setListStats({ total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0 });
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    fetchRequests(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when page or page size changes
  }, [page, pageSize]);

  const formik = useFormik({
    initialValues: {
      teacherId: "",
      leaveTypeId: "",
      startDate: "",
      endDate: "",
      reason: "",
      status: "Pending",
    },
    validate: (values) => {
      const errors = {};
      if (!values.teacherId) errors.teacherId = requiredMessage;
      if (!values.leaveTypeId) errors.leaveTypeId = requiredMessage;
      if (!values.startDate) errors.startDate = requiredMessage;
      if (!values.endDate) errors.endDate = requiredMessage;
      if (!values.reason) errors.reason = requiredMessage;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      setSubmitError("");
      setSaving(true);
      try {
        const formData = new FormData();
        formData.append("TeacherId", values.teacherId);
        formData.append("LeaveTypeId", values.leaveTypeId);
        formData.append("StartDate", values.startDate);
        formData.append("EndDate", values.endDate);
        formData.append("Reason", values.reason);

        await api.post(leaveCreateUrl, formData, {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        });
        resetForm();
        setOpenForm(false);
        setPage(1);
        fetchRequests(1);
      } catch (err) {
        setSubmitError(
          err?.response?.data?.message || "Could not create leave request."
        );
      } finally {
        setSaving(false);
      }
    },
  });

  const renderFieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <SoftTypography variant="caption" color="error">
        {formik.errors[name]}
      </SoftTypography>
    ) : null;

  const handleStatusUpdate = async () => {
    if (!statusTargetId) return;
    const status = statusValue || "Pending";
    setUpdateError("");
    setUpdating((prev) => ({ ...prev, [statusTargetId]: true }));
    try {
      const formData = new FormData();
      formData.append("Id", statusTargetId);
      formData.append("Status", status);

      await api.post(leaveUpdateUrl, formData, {
        headers: {
          ...authHeader,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchRequests(page);
      setStatusModalOpen(false);
    } catch (err) {
      setUpdateError(
        err?.response?.data?.message || "Failed to update status."
      );
    } finally {
      setUpdating((prev) => ({ ...prev, [statusTargetId]: false }));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }}>
        <SoftBox mb={3}>
          <Card
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
              border: "none",
              boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)",
            }}
          >
            <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                p: { xs: 2.25, md: 3 },
              }}
            >
              <SoftBox sx={{ minWidth: 0, flex: "1 1 240px" }}>
                <SoftTypography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    color: figmaTokens.textMuted,
                    fontSize: "0.68rem",
                  }}
                >
                  {t("leave.page_kicker", "LEAVE")}
                </SoftTypography>
                <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                  {t("leave.title", "Demandes de congé")}
                </SoftTypography>
                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
                  {t(
                    "leave.page_hint",
                    "Track requests and update statuses from this view."
                  )}
                </SoftTypography>
              </SoftBox>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setOpenForm(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.75,
                  py: 1.1,
                  borderRadius: "12px",
                  color: "#fff",
                  background: figmaTokens.gradientCSS,
                  boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                  "&:hover": {
                    filter: "brightness(1.06)",
                    boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                  },
                }}
              >
                {t("leave.add", "Ajouter une demande")}
              </Button>
            </SoftBox>
          </Card>
        </SoftBox>

        <SoftBox
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
            mb: 3,
          }}
        >
          <SoftBox sx={leaveStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("leave.stats.total", "Requests") }}
              count={leaveStats.total}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "info", component: "assignment" }}
            />
          </SoftBox>
          <SoftBox sx={leaveStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("leave.stats.pending", "Pending") }}
              count={leaveStats.pending}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "warning", component: "pending_actions" }}
            />
          </SoftBox>
          <SoftBox sx={leaveStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("leave.stats.approved", "Approved") }}
              count={leaveStats.approved}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "success", component: "check_circle" }}
            />
          </SoftBox>
          <SoftBox sx={leaveStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("leave.stats.rejected", "Rejected") }}
              count={leaveStats.rejected}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "error", component: "cancel" }}
            />
          </SoftBox>
          <SoftBox sx={leaveStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("leave.stats.cancelled", "Cancelled") }}
              count={leaveStats.cancelled}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "dark", component: "block" }}
            />
          </SoftBox>
        </SoftBox>

        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
            bgcolor: figmaTokens.surface,
          }}
        >
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("leave.table_title", "All requests")}
            </SoftTypography>
            {tableRangeLabel ? (
              <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 600 }}>
                {tableRangeLabel}
              </SoftTypography>
            ) : null}
          </SoftBox>

          {error && (
            <SoftBox px={2.5} pb={2} pt={1}>
              <Alert severity="error">{error}</Alert>
            </SoftBox>
          )}
          {updateError && (
            <SoftBox px={2.5} pb={2} pt={0}>
              <Alert severity="error">{updateError}</Alert>
            </SoftBox>
          )}

          <SoftBox>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 0,
                border: "none",
                bgcolor: "transparent",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 960,
                  ...directoryTableHeadStickyOverridesSx,
                  ...directoryTableBodyCellDefaultsSx,
                }}
                aria-label={t("leave.title", "Demandes de congé")}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.teacher", "Nom de l'enseignant")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.type", "Type de congé")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.start", "Date de début")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.end", "Date de fin")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.reason", "Motif")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.status", "Statut")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("leave.reviewed", "Date de validation")}</TableCell>
                    <TableCell align="right" sx={directoryTableHeaderCellSx}>{t("leave.actions", "Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && requests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                          {t("leave.none", "Aucune demande trouvée.")}
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {requests.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.85)" },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{row.teacherName ?? "--"}</TableCell>
                        <TableCell>{row.leaveType ?? row.leaveTypeId ?? "--"}</TableCell>
                        <TableCell>{formatDate(row.startDate)}</TableCell>
                        <TableCell>{formatDate(row.endDate)}</TableCell>
                        <TableCell title={row.reason ?? ""} sx={{ maxWidth: 220 }}>
                          {truncateText(row.reason)}
                        </TableCell>
                        <TableCell>
                          <Chip label={renderStatus(row.status)} size="small" sx={statusChipSx(row.status)} />
                        </TableCell>
                        <TableCell>{formatDate(row.reviewedOn)}</TableCell>
                        <TableCell align="right">
                          <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => openDetail(row.id)}
                              title={t("leave.view", "Voir")}
                              sx={{
                                color: figmaTokens.textSecondary,
                                "&:hover": { color: figmaTokens.accent, bgcolor: "rgba(102, 126, 234, 0.08)" },
                              }}
                            >
                              <Icon fontSize="small">visibility</Icon>
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => openStatusModal(row)}
                              title={t("leave.update_status", "Mettre à jour")}
                              sx={{
                                color: figmaTokens.textSecondary,
                                "&:hover": { color: figmaTokens.accent, bgcolor: "rgba(102, 126, 234, 0.08)" },
                              }}
                            >
                              <Icon fontSize="small">rule</Icon>
                            </IconButton>
                          </SoftBox>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationBar
              page={page}
              totalCount={totalCount}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeLabel={t("pagination.page_size", "Rows per page")}
            />
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ p: 0 }}>
          <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
            <SoftTypography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: figmaTokens.textMuted,
                fontSize: "0.68rem",
              }}
            >
              {t("leave.detail_kicker", "DETAIL")}
            </SoftTypography>
            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("leave.details", "Détails de la demande")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
              {t(
                "leave.detail_hint",
                "Summary of this request and reason."
              )}
            </SoftTypography>
            <IconButton
              aria-label="close"
              onClick={() => setDetailOpen(false)}
              sx={{
                position: "absolute",
                right: 10,
                top: 14,
                color: figmaTokens.textSecondary,
                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 3, pt: 0 }}>
          {detailLoading && (
            <SoftBox py={5} display="flex" justifyContent="center">
              <Loading />
            </SoftBox>
          )}
          {detailError && (
            <SoftBox mb={2}>
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {detailError}
              </Alert>
            </SoftBox>
          )}
          {!detailLoading && !detailError && detailData && (
            <SoftBox display="grid" gap={2}>
              <SoftBox
                display="grid"
                gap={2}
                sx={{
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                }}
              >
                <DetailRow label={t("leave.teacher", "Nom de l'enseignant")}>
                  {detailData.teacherName ?? "--"}
                </DetailRow>
                <DetailRow label={t("leave.type", "Type de congé")}>
                  {detailData.leaveType ?? detailData.leaveTypeId ?? "--"}
                </DetailRow>
                <DetailRow label={t("leave.start", "Date de début")}>
                  {formatDate(detailData.startDate)}
                </DetailRow>
                <DetailRow label={t("leave.end", "Date de fin")}>
                  {formatDate(detailData.endDate)}
                </DetailRow>
                <DetailRow label={t("leave.status", "Statut")}>
                  <Chip
                    label={renderStatus(detailData.status)}
                    size="small"
                    sx={statusChipSx(detailData.status)}
                  />
                </DetailRow>
                <DetailRow label={t("leave.reviewed", "Date de validation")}>
                  {formatDate(detailData.reviewedOn)}
                </DetailRow>
              </SoftBox>
              <DetailRow label={t("leave.reason", "Motif")}>
                <SoftTypography
                  component="div"
                  variant="body2"
                  sx={{
                    color: figmaTokens.textPrimary,
                    whiteSpace: "pre-wrap",
                    fontWeight: 500,
                    lineHeight: 1.6,
                  }}
                >
                  {detailData.reason ?? "--"}
                </SoftTypography>
              </DetailRow>
            </SoftBox>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ p: 0 }}>
          <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ px: 2.5, py: 2, pr: 7, position: "relative" }}>
            <SoftTypography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: figmaTokens.textMuted,
                fontSize: "0.68rem",
              }}
            >
              {t("leave.status", "Statut")}
            </SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("leave.status_title", "Mettre à jour le statut")}
            </SoftTypography>
            <IconButton
              aria-label="close"
              onClick={() => setStatusModalOpen(false)}
              sx={{
                position: "absolute",
                right: 10,
                top: 14,
                color: figmaTokens.textSecondary,
                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 2, pt: 1 }}>
          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mb: 2 }}>
            {t("leave.status_hint", "Choisissez un statut pour cette demande.")}
          </SoftTypography>
          <SoftTypography component="label" sx={{ ...fieldLabelSx, mb: 1 }}>
            {t("leave.status", "Statut")}
          </SoftTypography>
          <Select2Field
            name="status"
            options={[
              { id: "", text: t("leave.status", "Statut") },
              ...STATUS_OPTIONS.map((status) => ({
                id: status,
                text: renderStatus(status),
              })),
            ]}
            value={statusValue}
            onChange={(val) => setStatusValue(val)}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
            bgcolor: figmaTokens.pageBg,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setStatusModalOpen(false)}
            disabled={Boolean(statusTargetId && updating[statusTargetId])}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.5,
              borderColor: figmaTokens.borderStrong,
              color: figmaTokens.textSecondary,
            }}
          >
            {t("leave.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleStatusUpdate}
            disabled={Boolean(statusTargetId && updating[statusTargetId])}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.75,
              py: 1,
              color: "#fff",
              background: figmaTokens.gradientCSS,
              boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
              "&:hover": {
                filter: "brightness(1.06)",
                boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
              },
            }}
          >
            {statusTargetId && updating[statusTargetId]
              ? t("leave.saving", "Enregistrement...")
              : t("leave.save", "Enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ p: 0 }}>
          <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
            <SoftTypography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: figmaTokens.textMuted,
                fontSize: "0.68rem",
              }}
            >
              {t("leave.modal_form_kicker", "NEW REQUEST")}
            </SoftTypography>
            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("leave.add_title", "Ajouter une demande de congé")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
              {t(
                "leave.modal_form_hint",
                "Fill required fields. Dates define the leave period."
              )}
            </SoftTypography>
            <IconButton
              aria-label="close"
              onClick={() => setOpenForm(false)}
              sx={{
                position: "absolute",
                right: 10,
                top: 14,
                color: figmaTokens.textSecondary,
                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 1, pt: 1 }}>
          <form id="leave-request-form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.teacher", "Nom de l'enseignant")} *
                </SoftTypography>
                <Select2Field
                  name="teacherId"
                  options={[
                    { id: "", text: t("leave.select_teacher", "Sélectionner un enseignant") },
                    ...teacherOpts,
                  ]}
                  value={formik.values.teacherId}
                  onChange={(val) => formik.setFieldValue("teacherId", val)}
                />
                {renderFieldError("teacherId")}
              </Grid>

              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.type", "Type de congé")} *
                </SoftTypography>
                <Select2Field
                  name="leaveTypeId"
                  options={[
                    { id: "", text: t("leave.select_type", "Sélectionner un type") },
                    ...leaveTypeOpts,
                  ]}
                  value={formik.values.leaveTypeId}
                  onChange={(val) => formik.setFieldValue("leaveTypeId", val)}
                />
                {renderFieldError("leaveTypeId")}
              </Grid>

              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.start", "Date de début")} *
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="startDate"
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik.touched.startDate && formik.errors.startDate
                  )}
                />
                {renderFieldError("startDate")}
              </Grid>

              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.end", "Date de fin")} *
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="endDate"
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik.touched.endDate && formik.errors.endDate
                  )}
                />
                {renderFieldError("endDate")}
              </Grid>

              <Grid item xs={12}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.reason", "Motif")} *
                </SoftTypography>
                <SoftInput
                  multiline
                  minRows={3}
                  name="reason"
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik.touched.reason && formik.errors.reason
                  )}
                  placeholder={t(
                    "leave.reason_placeholder",
                    "Ajoutez un motif court pour cette demande"
                  )}
                />
                {renderFieldError("reason")}
              </Grid>

              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={fieldLabelSx}>
                  {t("leave.status", "Statut")}
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formik.values.status}
                    onChange={(e) =>
                      formik.setFieldValue("status", e.target.value)
                    }
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {renderStatus(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: "12px" }}>
                    {submitError}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
            bgcolor: figmaTokens.pageBg,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setOpenForm(false)}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.5,
              borderColor: figmaTokens.borderStrong,
              color: figmaTokens.textSecondary,
            }}
          >
            {t("leave.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            type="submit"
            form="leave-request-form"
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.75,
              py: 1,
              color: "#fff",
              background: figmaTokens.gradientCSS,
              boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
              "&:hover": {
                filter: "brightness(1.06)",
                boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
              },
            }}
          >
            {saving ? t("leave.saving", "Enregistrement...") : t("leave.save", "Enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default LeaveRequests;

function DetailRow({ label, children }) {
  const body =
    typeof children === "string" || typeof children === "number" ? (
      <SoftTypography variant="body2" sx={{ color: figmaTokens.textPrimary, fontWeight: 500 }}>
        {children}
      </SoftTypography>
    ) : (
      children
    );

  return (
    <SoftBox
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        bgcolor: figmaTokens.pageBg,
        transition: "border-color 0.2s ease",
        "&:hover": { borderColor: "rgba(118, 75, 162, 0.22)" },
      }}
    >
      <SoftTypography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.75,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: figmaTokens.textMuted,
        }}
      >
        {label}
      </SoftTypography>
      <SoftBox sx={{ minWidth: 0 }}>{body}</SoftBox>
    </SoftBox>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};



